import React from 'react'
import { css } from '@emotion/core'
import { colors } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { marginTop } from '../../util/CssUtil'
import CommonPerson from '../common/CommonPerson'

const respTable = css`
  display: table;
  width: 60%;
  height: -webkit-fit-content;
`

const respTableCaption = css`
  display: table-caption;
  font-weight: bold;
  text-decoration: underline;
  margin-bottom: 0.5em;
  margin-top: 2em;

  &[variant='single'] {
    padding-inline-start: 2em;
  }

  &[variant='double'] {
    padding-inline-start: 12em;
  }
`

const respTableBody = css`
  display: table-row-group;
`

const respTableRow = css`
  display: table-row;
  border: 1px solid red;
`

const tableBodyCell = css`
  display: table-cell;
  width: 25em;
  max-height: 40px;
  vertical-align: bottom;
`

const badge = css`
    border-radius: 25px;
    margin 2px;
    padding:3px;
    background-color: ${colors.blue50};
    color: white;
  `

const SpouseBadge = ({ isSpouse, t }) => isSpouse && <span css={badge}>&nbsp;{t('spouse', 'Spouse')}&nbsp;</span>

const LeftPerson = ({ person, t }) => (
  <div css={[tableBodyCell]} variant="left">
    <SpouseBadge isSpouse={person.spouse} t={t} />
    <CommonPerson name={`${person.name} (${person.lifespan})`} gender={person.gender} personId={person.id} />
  </div>
)

const RightPerson = ({ person, t }) => (
  <div css={[tableBodyCell]} variant="right">
    <SpouseBadge isSpouse={person.spouse} t={t} />
    <CommonPerson name={`${person.name} (${person.lifespan})`} gender={person.gender} personId={person.id} />
  </div>
)

const Filler = () => <div css={[tableBodyCell]} />

const RelationshipTree = ({ generations, t }) => {
  if (!generations) {
    return null
  }
  if (generations[0].leftPerson && generations[0].rightPerson) {
    return (
      <div css={respTableBody}>
        {generations.map((generation) => (
          <div key={`generation${generation.leftPerson ? generation.leftPerson.id : generation.rightPerson.id}`}>
            <div css={respTableRow}>
              {generation.leftPerson ? <LeftPerson person={generation.leftPerson} t={t} /> : <Filler />}
              {generation.rightPerson ? <RightPerson person={generation.rightPerson} t={t} /> : <Filler />}
            </div>
          </div>
        ))}
      </div>
    )
  }
  if (generations[0].leftPerson) {
    return (
      <div css={respTableBody}>
        {generations.map((generation) => (
          <div key={`generation${generation.leftPerson.id}`}>
            <div css={respTableRow}>
              <LeftPerson key={generation.leftPerson.id} person={generation.leftPerson} t={t} />
            </div>
          </div>
        ))}
      </div>
    )
  }
  if (generations[0].rightPerson) {
    return (
      <div css={respTableBody}>
        {generations.map((generation) => (
          <div key={`generation${generation.rightPerson.id}`}>
            <div css={respTableRow}>
              <RightPerson key={generation.rightPerson.id} person={generation.rightPerson} t={t} />
            </div>
          </div>
        ))}
      </div>
    )
  }
  return null
}

export default function RelationshipChart({ relationships }) {
  const { t } = useTranslation()
  const captionPosition =
    relationships.generations?.[0]?.leftPerson && relationships.generations?.[0]?.rightPerson ? 'double' : 'single'

  return (
    <>
      {relationships && (
        <div css={marginTop('20px')}>
          <h5>{`${t('relationship.label', 'Relationship')}: ${relationships.relationSummary}`}</h5>
          <div css={respTable}>
            {relationships.commonAncestor && (
              <div css={respTableCaption} variant={captionPosition}>
                <CommonPerson
                  name={`${relationships.commonAncestor.name} (${relationships.commonAncestor.lifespan})`}
                  gender={relationships.commonAncestor.gender}
                  personId={relationships.commonAncestor.id}
                />
              </div>
            )}
            <RelationshipTree generations={relationships.generations} t={t} />
          </div>
        </div>
      )}
    </>
  )
}
